import React, { useEffect } from 'react';

import { type Language, AppConfigUpdateModel } from '@flipdish/api-client-typescript';
import type { GetBrandsByOrgId200Response, Org } from '@flipdish/orgmanagement';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';

import { permissionsSelector } from '../../../selectors/permissions.selector';
import { flagService } from '../../../services';
import PageLayout from '../../../ui/Layout';
import { getBrandsForOrgKey, getOrgByBrandId, uploadImage } from '../../RMS/organisation.services';
import { setCurrentOrg } from '../../RMS/rms.actions';
import {
  getSMSRestaurantName,
  setAppConfig,
  setAppConfigLanguages,
  setSMSRestaurantName,
  uploadAppConfigLogo,
} from '../../WebsiteAndApp/actions';
import { getSMSRestaurantNameSelector } from '../../WebsiteAndApp/selectors';
import { getBrandById, getBrandByIdKey, updateBrand } from '../Brands/brands.services';
import { senderIdsKeys, useOrgSenderIds } from '../hooks/useOrgSenderIds';
import { putTwilioSmsRegistration } from '../settings.service';
import { PutTwilioSmsRegistrationRequestBody } from '../settings.types';
import GeneralSettingsForm, { type FormValues } from './GeneralSettingsForm';

const CATEGORIES: { [key: string]: AppConfigUpdateModel.ApplicationCategoryEnum } = {
  '0': AppConfigUpdateModel.ApplicationCategoryEnum.Restaurant,
  '1': AppConfigUpdateModel.ApplicationCategoryEnum.Cafe,
  '2': AppConfigUpdateModel.ApplicationCategoryEnum.Convenience,
};

const isLanguagesEqual = (newLanguages: Language[], previousLanguages: Language[]): boolean => {
  if (newLanguages.length !== previousLanguages.length) {
    return false;
  }

  return newLanguages.every((item, index) => {
    const prevItem = previousLanguages[index];

    return prevItem && item.LanguageId === prevItem.LanguageId;
  });
};

const hasChanges = (
  newConfig: AppConfigUpdateModel,
  previousConfig: AppConfigUpdateModel
): boolean => {
  return Object.keys(newConfig).some((key) => {
    const newValue = newConfig[key];
    let previousValue = previousConfig[key];

    if (key === 'ApplicationCategory' && previousValue !== undefined) {
      previousValue = CATEGORIES[previousValue];
    }

    return newValue !== previousValue;
  }, null);
};

type Props = MappedProps & MappedDispatch;

const GeneralSettings = (props: Props) => {
  const {
    currentApp,
    currentOrg,
    getSMSRestaurantName,
    isFlipdishStaff,
    isSmsRegistrationEnabled,
    setCurrentOrg,
    setLanguages,
    setSMSRestaurantName,
    smsRestaurantName,
    translate,
    updateConfig,
    uploadLogo,
  } = props;
  const { MainColor, Name, Languages, ApplicationCategory, IsPanaceaEnabled = false } = currentApp;

  const queryClient = useQueryClient();
  // Cached if navigated from list screen
  const brandCached = queryClient
    .getQueryData<{ data: GetBrandsByOrgId200Response }>([getBrandsForOrgKey, currentOrg?.orgId])
    ?.data?.data?.find((brand) => brand.brandId === currentApp.AppId);

  // Fetch data if page loaded directly
  const { data: brandData } = useQuery({
    queryKey: [getBrandByIdKey, currentApp.AppId, currentOrg?.orgId],
    queryFn: () =>
      getBrandById({ brandId: currentApp.AppId ?? '', orgId: currentOrg?.orgId ?? '' }),
    enabled: !brandCached && !!currentApp.AppId && !!currentOrg?.orgId,
  });

  const brand = brandCached ?? brandData?.data?.data;

  const { data: senderIds = [], isLoading: isSenderIdsLoading } = useOrgSenderIds(
    currentOrg?.orgId ?? '',
    {
      brandId: currentApp.AppId,
    },
    {
      isEnabled: isSmsRegistrationEnabled && !!currentOrg?.orgId,
    }
  );

  useEffect(() => {
    if (!isSmsRegistrationEnabled) {
      getSMSRestaurantName();
    }
  }, []);



  const { data: orgData } = useQuery({
    queryKey: [getOrgByBrandId, currentApp.AppId],
    queryFn: () => getOrgByBrandId(currentApp.AppId || ''),
    enabled: !currentOrg?.orgId && !!currentApp.AppId,
    retry: 1,
  });

  useEffect(() => {
    orgData?.data?.data?.[0] && setCurrentOrg(orgData?.data?.data?.[0]);
  }, [orgData]);

  const saveSettings = async ({
    isArchived,
    IsPanaceaEnabled,
    languages,
    logo,
    brandCoverPhoto,
    name,
    senderId,
    SMSRestaurantName,
    type,
    webAndAppsButtonColour,
  }: FormValues & { logo?: File; brandCoverPhoto?: File }) => {
    const newLanguages = languages.map((language) => {
      return {
        LanguageId: language.value,
        Name: language.label,
        DisplayOrder: language.displayOrder,
      };
    }) as Language[];

    // If SMS registration is enabled and a sender ID is provided, update the sender ID
    // If SMS registration is disabled, set the SMS restaurant name
    if (isSmsRegistrationEnabled && senderId) {
      const requestPayload: PutTwilioSmsRegistrationRequestBody = {
        orgId: currentOrg?.orgId ?? '',
        brandId: currentApp.AppId ?? '',
        registeredSenderId: senderId.id,
        countryCode: currentApp.CountryId ?? '',
        status: senderId.status,
        isActive: true,
      };
      await putTwilioSmsRegistration(requestPayload);
      queryClient.invalidateQueries({
        queryKey: senderIdsKeys.byOrgAndBrand(
          currentOrg?.orgId ?? '',
          currentApp.AppId ?? undefined
        ),
      });
    } else if (!isSmsRegistrationEnabled) {
      await setSMSRestaurantName(SMSRestaurantName);
    }

    let logoImageUrl = currentApp.LogoImageUrl;
    if (logo) {
      if (currentOrg?.orgId) {
        const formDataOrg = new FormData();
        formDataOrg.append('file', logo);
        logoImageUrl = await uploadImage(currentOrg?.orgId, formDataOrg);
      } else {
        const formData = new FormData();
        formData.append('files[]', logo);
          uploadLogo(formData);
      }
    }

    let brandCoverPhotoUrl = brand?.coverPhotoUrl;
    if (brandCoverPhoto) {
      if (currentOrg?.orgId) {
        const formData = new FormData();
        formData.append('file', brandCoverPhoto);
        brandCoverPhotoUrl = await uploadImage(currentOrg?.orgId, formData);
      }
    }

    if (!isLanguagesEqual(newLanguages, currentApp.Languages || [])) {
      await setLanguages(newLanguages);
    }

    const config: AppConfigUpdateModel = {
      Name: name,
      IsPanaceaEnabled,
      ApplicationCategory: type,
      MainColor: webAndAppsButtonColour.slice(1),
      LogoImageUrl: logoImageUrl,
      CountryId: currentApp.CountryId,
    };

    if (hasChanges(config, currentApp)) {
      await updateConfig(config);
    }

    if (currentOrg?.orgId && currentApp.AppId) {
      await updateBrand({
        brand: {
          name: name,
          logoImageUrl: logoImageUrl,
          coverPhotoUrl: brandCoverPhotoUrl,
          brandId: currentApp.AppId,
          countryCode: currentApp.CountryId as any,
          isArchived: isArchived ?? brand?.isArchived ?? false,
        },
        brandId: currentApp.AppId,
        orgId: currentOrg?.orgId,
      });
      queryClient.invalidateQueries({ queryKey: [getBrandsForOrgKey, currentOrg?.orgId] });
      queryClient.invalidateQueries({
        queryKey: [getBrandByIdKey, currentApp.AppId, currentOrg?.orgId],
      });
    }
  };

  return (
    <PageLayout
      auditLogsFilter={{ type: 'EventType', value: 'app.*' }}
      documentTitle="Brand_Settings"
      title={translate('Brand_Settings')}
      toParent={`/${currentApp.AppId}/settings/brands`}
      strictToParent
    >
      <GeneralSettingsForm
        applicationCategory={ApplicationCategory}
        brandId={currentApp.AppId}
        brandName={Name}
        isArchived={brand?.isArchived ?? false}
        isFlipdishStaff={isFlipdishStaff}
        isPanaceaEnabled={IsPanaceaEnabled}
        isSenderIdsLoading={isSenderIdsLoading}
        isSmsRegistrationEnabled={isSmsRegistrationEnabled}
        languages={Languages}
        logo={brand?.logoImageUrl}
        brandCoverPhoto={brand?.coverPhotoUrl}
        senderIds={senderIds}
        smsRestaurantName={smsRestaurantName}
        submit={saveSettings}
        webAndAppsButtonColour={MainColor}
      />
    </PageLayout>
  );
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  setSMSRestaurantName: (smsRestaurantName: string) =>
    dispatch(setSMSRestaurantName(smsRestaurantName)),
  getSMSRestaurantName: () => dispatch(getSMSRestaurantName()),
  updateConfig: (config: AppConfigUpdateModel) => dispatch(setAppConfig(config)),
  setLanguages: (languages: Language[]) => dispatch(setAppConfigLanguages(languages)),
  setCurrentOrg: (org: Org) => dispatch(setCurrentOrg(org)),
  uploadLogo: (data: FormData) => dispatch(uploadAppConfigLogo(data)),
});

type MappedProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  const getFlipdishStaffSelector = permissionsSelector.hasPermissionFactory(['FlipdishStaff']);
  return {
    currentApp: state.currentApp,
    currentOrg: state.rms.currentOrg,
    isFlipdishStaff: getFlipdishStaffSelector(state),
    isSmsRegistrationEnabled:
      flagService.isFlagOn(state, 'smsRegistration') && state.currentApp.CountryId === 'IE',
    smsRestaurantName: getSMSRestaurantNameSelector(state),
    translate: getTranslate(state.locale),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(GeneralSettings);
